import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertTriangle, Lock } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useDeleteAccount } from '../../hooks/useProfile';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteAccountModal = ({ isOpen, onClose, userName }) => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const deleteAccount = useDeleteAccount();
    const { t } = useLanguage();

    const handleClose = () => {
        setPassword('');
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!password.trim()) {
            setError(t(KEYS.DELETE_ACCOUNT_PASSWORD_REQUIRED));
            return;
        }

        setLoading(true);

        try {
            await deleteAccount.mutateAsync(password);
            navigate('/login');
        } catch (err) {
            setError(err.message || t(KEYS.DELETE_ACCOUNT_ERROR));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.DELETE_ACCOUNT_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="medium"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={loading}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleSubmit} disabled={loading}>
                        {loading ? t(KEYS.COMMON_DELETING) : t(KEYS.DELETE_ACCOUNT_SUBMIT)}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Warning Box */}
                <div className="flex gap-3 p-4 bg-status-overdue-bg border border-status-overdue/20 rounded">
                    <AlertTriangle className="w-6 h-6 text-status-overdue flex-shrink-0" />
                    <div>
                        <p className="text-sm font-bold text-status-overdue mb-1">
                            {t(KEYS.DELETE_ACCOUNT_WARNING_TITLE)}
                        </p>
                        <p className="text-sm text-status-overdue/80 leading-relaxed">
                            {t(KEYS.DELETE_ACCOUNT_BODY, { name: userName })}
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm animate-appear">
                        {error}
                    </div>
                )}

                <div className="space-y-1">
                    <label htmlFor="password" className="label-base">
                        {t(KEYS.DELETE_ACCOUNT_PASSWORD_LABEL)} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`input-base pl-10 ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
                            disabled={loading}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default DeleteAccountModal;
